import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import Modal from '@components/shared/Modal'
import PecasAcervo from '@services/pecasAcervo'
import AcervoFields from './AcervoFields'
import useAcervoCatalog from './useAcervoCatalog'
import { extractPecaPdf } from './extractPecaPdf'
import { actionLabel, areaLabel, EVENT_LABELS, fieldsFromDetail, formatBytes, formatDate,
  indexingMessage, isCanonicalAction, MAX_TEXT_CHARACTERS, mutationError, STATUS_LABELS, validateFile } from './acervoHelpers'

export default function PecasAcervoEditor({ initialDetail, initialArea, onClose, onChanged }) {
  const [detail, setDetail] = useState(initialDetail)
  const [fields, setFields] = useState(() => ({ ...fieldsFromDetail(initialDetail), ...(!initialDetail ? { area: initialArea || '' } : {}) }))
  const [saved, setSaved] = useState(() => JSON.stringify(fieldsFromDetail(initialDetail)))
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [progress, setProgress] = useState(null)
  const [notices, setNotices] = useState([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingExit, setPendingExit] = useState(null)
  const [archiveConfirm, setArchiveConfirm] = useState(false)
  const [reloadConfirm, setReloadConfirm] = useState(false)
  const [catalogRevision, setCatalogRevision] = useState(0)
  const extraction = useRef(null)
  const operation = useRef(null)
  const detailRef = useRef(detail)
  detailRef.current = detail
  const catalog = useAcervoCatalog(fields.area, catalogRevision)
  const documento = detail?.documento
  const isNew = !documento
  const canonicalAction = !catalog.loading && !catalog.error && isCanonicalAction(fields.acao, catalog.acoesCanonicas)
  const classificationChanged = isNew || fields.acao !== documento.acao || fields.area !== documento.area
  const classificationValid = !classificationChanged || canonicalAction
  const archived = documento?.status === 'ARQUIVADO'
  const dirty = isNew ? Boolean(file || fields.titulo || fields.acao || fields.observacoes) : JSON.stringify(fields) !== saved
  const locked = Boolean(busy || extracting)
  const leaseExpired = documento?.status === 'INDEXANDO' && documento.indexacaoExpiraEm && new Date(documento.indexacaoExpiraEm).valueOf() <= Date.now()
  const indexing = documento?.status === 'INDEXANDO' && !leaseExpired
  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) =>
    Boolean(dirty || locked) && (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash
    ))

  const applyDetail = useCallback((next) => {
    if (!next?.documento) return
    const current = detailRef.current
    if (current?.documento.id === next.documento.id && current.documento.version > next.documento.version) return
    detailRef.current = next
    setDetail(next)
    const nextFields = fieldsFromDetail(next)
    setFields(nextFields)
    setSaved(JSON.stringify(nextFields))
  }, [])

  useEffect(() => () => { extraction.current?.abort(); operation.current?.abort() }, [])
  useEffect(() => {
    if (!dirty && !locked) return undefined
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = '' }
    const beforeLogout = (event) => {
      if (typeof event.detail?.proceed !== 'function') return
      event.preventDefault()
      setPendingExit({ proceed: event.detail.proceed })
    }
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('auth:before-logout', beforeLogout)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('auth:before-logout', beforeLogout)
    }
  }, [dirty, locked])

  useEffect(() => {
    if (!documento?.id || dirty || (busy !== 'index' && documento.status !== 'INDEXANDO')) return undefined
    const controller = new AbortController()
    const refresh = async () => {
      const result = await PecasAcervo.get(documento.id, controller.signal)
      if (!controller.signal.aborted && result.success) applyDetail(result.data)
    }
    const timer = setInterval(refresh, 20000)
    return () => { clearInterval(timer); controller.abort() }
  }, [documento?.id, documento?.status, busy, dirty, applyDetail])

  const leave = () => {
    if (dirty || locked) setPendingExit({ proceed: onClose })
    else onClose()
  }
  const cancelExit = () => {
    setPendingExit(null)
    if (navigationBlocker.state === 'blocked') navigationBlocker.reset()
  }
  const confirmExit = () => {
    extraction.current?.abort()
    operation.current?.abort()
    const proceed = pendingExit?.proceed
    setPendingExit(null)
    if (navigationBlocker.state === 'blocked') navigationBlocker.proceed()
    else proceed?.()
  }

  const selectFile = async (event) => {
    const nextFile = event.target.files?.[0]
    event.target.value = ''
    if (!nextFile) return
    const validation = validateFile(nextFile)
    if (validation) { setError(validation); return }
    extraction.current?.abort()
    const controller = new AbortController()
    extraction.current = controller
    setFile(nextFile)
    setError('')
    setNotices([])
    setProgress(null)
    setExtracting(true)
    setFields((current) => ({ ...current, titulo: current.titulo || nextFile.name.replace(/\.[^.]+$/, ''), conteudoTexto: '' }))
    try {
      const result = nextFile.name.toLowerCase().endsWith('.pdf')
        ? await extractPecaPdf(nextFile, { signal: controller.signal, onProgress: setProgress })
        : await PecasAcervo.extract(nextFile, controller.signal).then((response) => {
          if (!response.success) throw new Error(response.error)
          return response.data
        })
      if (controller.signal.aborted) return
      setFields((current) => ({ ...current, conteudoTexto: result.conteudoTexto || '' }))
      setNotices(result.avisos || [])
    } catch (failure) {
      if (controller.signal.aborted) setNotices(['Leitura cancelada. Selecione o arquivo novamente para repetir a extração.'])
      else setError(failure.message || 'Não foi possível extrair o texto deste arquivo.')
    } finally {
      if (extraction.current === controller) { setExtracting(false); setProgress(null) }
    }
  }

  const indexDocument = async (target) => {
    setBusy('index')
    const result = await PecasAcervo.index(target.id, target.version, operation.current.signal)
    if (operation.current.signal.aborted) return
    if (result.success) {
      applyDetail(result.data)
      if (result.data.documento.status === 'FALHOU') setError(indexingMessage(result.data.documento.erroCodigo))
      else if (result.data.documento.status === 'PRONTO') setSuccess('Indexação concluída. A peça está disponível para redação na área e ação informadas.')
      else setSuccess('O estado da indexação foi atualizado.')
    } else {
      setError(`${mutationError(result)} Atualize o estado para conferir o resultado da tentativa.`)
      const latest = await PecasAcervo.get(target.id, operation.current.signal)
      if (latest.success && !operation.current.signal.aborted) applyDetail(latest.data)
    }
    onChanged()
  }

  const save = async (andIndex = false) => {
    if (!fields.titulo.trim() || !fields.area || !fields.acao.trim()) {
      setError('Informe o título, a área jurídica e a ação da peça.')
      return
    }
    if (isNew && !file) { setError('Selecione o arquivo original antes de salvar.'); return }
    if (!classificationValid || (andIndex && !canonicalAction)) {
      setError('Selecione uma ação ativa das configurações do motor antes de salvar a classificação ou indexar.')
      return
    }
    setBusy('save')
    setError('')
    setSuccess('')
    operation.current = new AbortController()
    try {
      const result = isNew
        ? await PecasAcervo.create(file, fields, operation.current.signal)
        : await PecasAcervo.update(documento.id, fields, documento.version, operation.current.signal)
      if (operation.current.signal.aborted) return
      if (!result.success) { setError(mutationError(result)); return }
      applyDetail(result.data)
      setFile(null)
      setNotices([])
      setSuccess('Documento salvo na base jurídica.')
      setCatalogRevision((current) => current + 1)
      onChanged()
      if (andIndex && result.data.conteudoTexto?.trim()) await indexDocument(result.data.documento)
    } finally { setBusy('') }
  }

  const index = async () => {
    if (!canonicalAction) { setError('Selecione e salve uma ação ativa do motor antes de indexar.'); return }
    setError('')
    setSuccess('')
    operation.current = new AbortController()
    try { await indexDocument(documento) } finally { setBusy('') }
  }

  const refresh = async () => {
    setReloadConfirm(false)
    setBusy('refresh')
    setError('')
    operation.current = new AbortController()
    try {
      const result = await PecasAcervo.get(documento.id, operation.current.signal)
      if (operation.current.signal.aborted) return
      if (result.success) { applyDetail(result.data); onChanged() }
      else setError(mutationError(result))
    } finally { setBusy('') }
  }

  const archive = async () => {
    setArchiveConfirm(false)
    setBusy('archive')
    setError('')
    operation.current = new AbortController()
    try {
      const result = await PecasAcervo.archive(documento.id, documento.version, !archived, operation.current.signal)
      if (operation.current.signal.aborted) return
      if (result.success) {
        applyDetail(result.data)
        setSuccess(archived ? 'Documento restaurado. Confira o texto e indexe novamente quando necessário.' : 'Documento arquivado e removido das referências para redação.')
        onChanged()
      } else setError(mutationError(result))
    } finally { setBusy('') }
  }

  const download = async () => {
    setBusy('download')
    operation.current = new AbortController()
    try {
      const result = await PecasAcervo.download(documento.id, operation.current.signal)
      if (operation.current.signal.aborted) return
      if (!result.success) { setError(mutationError(result)); return }
      const url = URL.createObjectURL(result.data)
      const link = document.createElement('a')
      link.href = url
      link.download = documento.arquivoNome || 'peca-original'
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } finally { setBusy('') }
  }

  return (
    <section className="acervo-section acervo-editor" aria-label="Configuração da peça">
      <div className="acervo-toolbar">
        <button className="acervo-button" onClick={leave}><i className="fas fa-arrow-left" aria-hidden="true" /> Voltar às peças</button>
        <span className="acervo-save-state">{isNew ? 'Rascunho · ainda não salvo' : dirty ? 'Alterações não salvas' : `Salvo · versão ${documento.version}`}</span>
        {!isNew && <span className={`acervo-status acervo-status-${documento.status.toLowerCase()}`}>{STATUS_LABELS[documento.status] || documento.status}</span>}
      </div>
      {error && <div className="acervo-notice acervo-error" role="alert">{error}</div>}
      {success && <div className="acervo-notice acervo-success" role="status">{success}</div>}
      {catalog.error && <div className="acervo-notice acervo-error" role="alert">{catalog.error} <button onClick={() => setCatalogRevision((current) => current + 1)}>Tentar novamente</button></div>}
      <div className="acervo-editor-grid">
        <div className="acervo-editor-main">
          <section className="acervo-panel">
            <div className="acervo-panel-heading"><i className="fas fa-folder-open" aria-hidden="true" /><h2>{isNew ? 'Adicionar peça à base jurídica' : 'Classificação da peça'}</h2></div>
            <p className="acervo-muted">A área jurídica e a ação definem em quais contextos esta peça será usada como referência.</p>
            {isNew && <div className="acervo-upload">
              <i className="fas fa-file-arrow-up" aria-hidden="true" />
              <strong>{file?.name || 'Selecione o arquivo original'}</strong>
              <span>{file ? formatBytes(file.size) : 'PDF, DOCX ou TXT · até 10 MB'}</span>
              <label className={`acervo-button ${locked ? 'is-disabled' : ''}`}>
                {file ? 'Trocar arquivo' : 'Escolher arquivo'}
                <input type="file" accept=".pdf,.docx,.txt" onChange={selectFile} disabled={locked} />
              </label>
              <small>Selecionar e visualizar não salva o documento. O cadastro será criado ao clicar em Salvar.</small>
            </div>}
            {extracting && <div className="acervo-notice" role="status"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> {progress ? `Texto lido: ${progress.page} de ${progress.total} páginas.` : 'Extraindo texto do arquivo…'} <button onClick={() => extraction.current?.abort()}>Cancelar leitura</button></div>}
            {notices.map((notice, index) => <p className="acervo-notice" key={index}>{notice}</p>)}
            <AcervoFields fields={fields} onChange={setFields} areas={catalog.areas} acoesCanonicas={catalog.acoesCanonicas}
              catalogLoading={catalog.loading} disabled={locked || archived || indexing} />
          </section>
          <section className="acervo-panel">
            <div className="acervo-panel-heading"><i className="fas fa-align-left" aria-hidden="true" /><h2>{isNew ? 'Prévia do texto extraído' : 'Texto para indexação'}</h2><span>{fields.conteudoTexto.length.toLocaleString('pt-BR')} caracteres</span></div>
            <p className="acervo-muted">{isNew ? 'Confira a extração. Após salvar, você poderá corrigir este texto sem alterar o arquivo original.' : 'Revise o conteúdo que a IA utilizará como referência. Salvar alterações de texto ou classificação exige nova indexação.'}</p>
            {file?.name.toLowerCase().endsWith('.pdf') && <p className="acervo-muted">PDFs são lidos no navegador, sem OCR. Se não houver texto selecionável, salve o original e adicione o texto depois.</p>}
            <textarea className="acervo-text" aria-label={isNew ? 'Prévia do texto extraído' : 'Texto para indexação'}
              value={fields.conteudoTexto} readOnly={isNew || archived || indexing} disabled={locked}
              maxLength={MAX_TEXT_CHARACTERS} onChange={(event) => setFields({ ...fields, conteudoTexto: event.target.value })}
              placeholder={isNew ? 'O texto selecionável do arquivo aparecerá aqui.' : 'Adicione o texto revisado da peça antes de indexar.'} spellCheck={false} />
          </section>
        </div>
        <aside className="acervo-editor-aside">
          <section className="acervo-panel acervo-actions-panel">
            <h2>{isNew ? 'Salvar na base' : 'Documento e indexação'}</h2>
            {busy && <p className="acervo-working" role="status"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> {busy === 'index' ? 'Aguardando a indexação na IA local…' : busy === 'save' ? 'Salvando documento…' : 'Processando…'}</p>}
            {isNew || !archived ? <>
              <button className="acervo-button acervo-primary" disabled={locked || indexing || !classificationValid || (!isNew && !dirty)} onClick={() => save(false)}><i className="fas fa-save" aria-hidden="true" /> {isNew ? 'Salvar documento' : 'Salvar alterações'}</button>
              {isNew && <button className="acervo-button" disabled={locked || !canonicalAction || !fields.conteudoTexto.trim()} onClick={() => save(true)}>Salvar e indexar</button>}
              {!isNew && <button className="acervo-button" disabled={locked || dirty || indexing || !canonicalAction || !fields.conteudoTexto.trim() || !fields.area} onClick={index}><i className="fas fa-magnifying-glass" aria-hidden="true" /> {documento.status === 'FALHOU' || leaseExpired ? 'Tentar indexar novamente' : 'Indexar documento'}</button>}
            </> : null}
            {!isNew && <>
              <button className="acervo-button" disabled={locked} onClick={() => dirty ? setReloadConfirm(true) : refresh()}><i className="fas fa-rotate" aria-hidden="true" /> {dirty ? 'Recarregar versão salva' : 'Atualizar estado'}</button>
              <button className="acervo-button" disabled={locked || !documento.arquivoNome} onClick={download}><i className="fas fa-download" aria-hidden="true" /> Baixar original</button>
              <button className="acervo-button acervo-subtle" disabled={locked || dirty || indexing} onClick={() => setArchiveConfirm(true)}>{archived ? 'Restaurar documento' : 'Arquivar documento'}</button>
              {dirty && <p className="acervo-muted">Salve suas alterações antes de indexar ou arquivar.</p>}
              {leaseExpired && <p className="acervo-notice">A tentativa ultrapassou o prazo de execução. Atualize o estado ou tente indexar novamente.</p>}
              {documento.status === 'FALHOU' && <p className="acervo-notice acervo-error">{indexingMessage(documento.erroCodigo)} {documento.erroCodigo && <small>Código: {documento.erroCodigo}</small>}</p>}
              {documento.status === 'SEM_TEXTO' && <p className="acervo-notice">O original está salvo. Adicione e salve o texto para habilitar a indexação.</p>}
              {documento.status === 'SEM_AREA' && <p className="acervo-notice">Classifique esta peça por área e ação antes de indexar.</p>}
              <dl className="acervo-document-meta">
                <div><dt>Arquivo original</dt><dd>{documento.arquivoNome || 'Original indisponível'}</dd></div>
                <div><dt>Tamanho</dt><dd>{formatBytes(documento.arquivoTamanho)}</dd></div>
                <div><dt>Adicionado por</dt><dd>{documento.createdByNome || `Usuário ${documento.createdByUserId}`}</dd></div>
                <div><dt>Adicionado em</dt><dd>{formatDate(documento.createdAt)}</dd></div>
                <div><dt>Atualizado em</dt><dd>{formatDate(documento.updatedAt)}</dd></div>
                <div><dt>Fragmentos indexados</dt><dd>{documento.quantidadeFragmentos}</dd></div>
              </dl>
              {documento.arquivoSha256 && <details className="acervo-hash"><summary>Integridade do original (SHA-256)</summary><code>{documento.arquivoSha256}</code></details>}
            </>}
            {isNew && <p className="acervo-muted">Salvar mantém o original na base jurídica. Indexar prepara o texto para a redação assistida.</p>}
          </section>
          {!isNew && <section className="acervo-panel"><h2>Histórico do documento</h2>
            {!detail.historico?.length ? <p className="acervo-muted">Nenhum evento disponível.</p> : <ol className="acervo-history">{detail.historico.map((event) => <li key={event.id}>
              <strong>{EVENT_LABELS[event.evento] || event.descricao || event.evento}</strong>
              <span>{event.actorNome || `Usuário ${event.actorUserId}`} · {formatDate(event.createdAt)}</span>
              <small>{areaLabel(catalog.areas, event.area)} / {actionLabel(event.acao, catalog.acoesCanonicas)}</small>
              {event.descricao && EVENT_LABELS[event.evento] && <p>{event.descricao}</p>}
              {event.codigo && <small>Código: {event.codigo}</small>}
            </li>)}</ol>}
          </section>}
        </aside>
      </div>
      <Modal isOpen={Boolean(pendingExit) || navigationBlocker.state === 'blocked'} onClose={cancelExit} title="Sair deste documento?" size="small" icon="fa-exclamation-triangle"
        footer={<><button className="btn btn-outline" onClick={cancelExit}>Continuar aqui</button><button className="btn btn-danger" onClick={confirmExit}>Sair e descartar rascunho</button></>}>
        <p>As alterações não salvas serão descartadas. Se uma operação já foi enviada, confira seu resultado na lista de peças ao retornar.</p>
      </Modal>
      <Modal isOpen={archiveConfirm} onClose={() => setArchiveConfirm(false)} title={archived ? 'Restaurar documento?' : 'Arquivar documento?'} size="small"
        footer={<><button className="btn btn-outline" onClick={() => setArchiveConfirm(false)}>Cancelar</button><button className="btn btn-primary" onClick={archive}>{archived ? 'Restaurar' : 'Arquivar'}</button></>}>
        <p>{archived ? 'O documento voltará à base jurídica. Confira seu estado de indexação após restaurar.' : 'O documento deixará de ser usado como referência para redação. O original e o histórico serão preservados.'}</p>
      </Modal>
      <Modal isOpen={reloadConfirm} onClose={() => setReloadConfirm(false)} title="Recarregar a versão salva?" size="small"
        footer={<><button className="btn btn-outline" onClick={() => setReloadConfirm(false)}>Manter minhas edições</button><button className="btn btn-danger" onClick={refresh}>Descartar edições e recarregar</button></>}>
        <p>Suas alterações locais serão substituídas pela última versão salva. Copie o texto que deseja preservar antes de continuar.</p>
      </Modal>
    </section>
  )
}
